import { useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
    Settings2,
    Shield,
    Server,
    FileText,
    RefreshCw,
    AlertTriangle,
    Lock,
    Puzzle,
    Database,
    Cloud,
    Terminal,
    ChevronDown,
    ChevronRight,
    ShieldCheck,
    Settings as SettingsIcon,
} from 'lucide-react';
import LicenseTab from '../components/LicenseTab';
import { toast } from '@/lib/toast';
import { errorMessage } from '@/lib/http';
import { settingsApi } from '../services/api';
import { useDialogs } from '@/components/dialogs/DialogProvider';
import {
    Banner, Button, Card, CardButton, EmptyState, IconButton, Input, ListPageSkeleton, MaskedValue, PageHeader, Switch, Tabs, Timestamp,
} from '@/components/ds-zta';
import { useUrlState } from '@/lib/useUrlState';
import { humanize, isRecord, normalizeAgentTokens, settingsPayload, type AgentToken, type Json } from '@/features/settings/settingsData';
import { BRAND } from '@/brand';
import CodeBlock from '@/components/CodeBlock';

/* ─── Tab definitions ───────────────────────────────── */
// Design eval F1 / decision D3 = A: every tab shows what settingsApi returns,
// nothing else. The Certificates tab (CA health, issuance policy) and the
// compliance cards are gone until the backend has endpoints for them;
// /settings?tab=certificates falls back to General.
const tabs = [
    { id: 'general', name: 'General', icon: Settings2 },
    { id: 'auth', name: 'Authentication', icon: Shield },
    { id: 'agents', name: 'Agents', icon: Server },
    { id: 'integrations', name: 'Integrations', icon: Puzzle },
    { id: 'license', name: 'License', icon: ShieldCheck },
    { id: 'audit', name: 'Audit & Logs', icon: FileText },
] as const;

type TabId = (typeof tabs)[number]['id'];

/* ─── Settings Page ─────────────────────────────────── */
export default function Settings() {
    // The open section lives in the URL (design eval F24): /settings?tab=license
    // links straight to it, and a refresh keeps it.
    const [url, setUrl] = useUrlState({ tab: 'general' });
    const activeTab: TabId = tabs.some((t) => t.id === url.tab) ? (url.tab as TabId) : 'general';

    return (
        <div className="animate-fade-in space-y-6">
            {/* Header */}
            <PageHeader icon={<SettingsIcon />} title="Settings" description={`Manage your ${BRAND.name} configuration`} />

            {/* Tab Nav */}
            <Card className="overflow-hidden">
                <Tabs value={activeTab} urlParam="tab" onChange={(v) => setUrl({ tab: v })} aria-label="Settings sections">
                    <Tabs.List className="overflow-x-auto px-2">
                        {tabs.map((tab) => {
                            const Icon = tab.icon;
                            return (
                                <Tabs.Tab key={tab.id} value={tab.id}>
                                    <span className="flex items-center gap-2 whitespace-nowrap">
                                        <Icon className="h-4 w-4" aria-hidden="true" />
                                        {tab.name}
                                    </span>
                                </Tabs.Tab>
                            );
                        })}
                    </Tabs.List>

                    {/* Tab Content */}
                    <Tabs.Panel value="general" unmountInactive className="p-6">
                        <GeneralTab />
                    </Tabs.Panel>
                    <Tabs.Panel value="auth" unmountInactive className="p-6">
                        <AuthTab />
                    </Tabs.Panel>
                    <Tabs.Panel value="agents" unmountInactive className="p-6">
                        <AgentsTab />
                    </Tabs.Panel>
                    <Tabs.Panel value="integrations" unmountInactive className="p-6">
                        <IntegrationsTab />
                    </Tabs.Panel>
                    <Tabs.Panel value="license" unmountInactive className="p-6">
                        <LicenseTab />
                    </Tabs.Panel>
                    <Tabs.Panel value="audit" unmountInactive className="p-6">
                        <AuditTab />
                    </Tabs.Panel>
                </Tabs>
            </Card>
        </div>
    );
}

/* ─── Reading what the settings endpoints return ────── */
// settingsApi's responses are untyped (services/api.ts), so the tabs draw
// whatever fields the server sends rather than a fixed mock layout: scalars as
// read-only fields, nested objects as sub-sections. A field whose name says it
// is a secret is masked (decision D4 = A).

const SECRET_KEY = /(secret|token|password|passwd|private[_-]?key|api[_-]?key|credential)/i;
const TIME_KEY = /(_at|_time|_date)$/i;

function scalarText(value: unknown): string {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'boolean') return value ? 'Enabled' : 'Disabled';
    if (Array.isArray(value)) return value.length ? value.map((v) => (isRecord(v) ? JSON.stringify(v) : String(v))).join(', ') : '—';
    return String(value);
}

function ConfigFields({ data, exclude = [] }: { data: Json; exclude?: string[] }) {
    const entries = Object.entries(data).filter(([k]) => !exclude.includes(k));
    const scalars = entries.filter(([, v]) => !isRecord(v));
    const nested = entries.filter(([, v]) => isRecord(v)) as [string, Json][];
    return (
        <div className="space-y-6">
            {scalars.length > 0 && (
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {scalars.map(([key, value]) => (
                        <ReadOnlyField key={key} label={humanize(key)}>
                            {SECRET_KEY.test(key) && typeof value === 'string' && value ? (
                                <MaskedValue value={value} kind="secret" label={humanize(key).toLowerCase()} />
                            ) : TIME_KEY.test(key) && typeof value === 'string' && value ? (
                                <Timestamp value={value} fallback={value} />
                            ) : (
                                scalarText(value)
                            )}
                        </ReadOnlyField>
                    ))}
                </dl>
            )}
            {nested.map(([key, value]) => (
                <div key={key}>
                    <h4 className="text-sm font-medium text-[var(--d2d-text-secondary)] mb-3">{humanize(key)}</h4>
                    <ConfigFields data={value} />
                </div>
            ))}
        </div>
    );
}

/** Loading, error and empty states shared by the API-backed tabs. */
function QueryStates({
    what,
    isLoading,
    isError,
    error,
    onRetry,
    empty,
    children,
}: {
    what: string;
    isLoading: boolean;
    isError: boolean;
    error?: unknown;
    onRetry: () => void;
    empty: boolean;
    children: ReactNode;
}) {
    if (isLoading) return <ListPageSkeleton aria-label={`Loading ${what}`} filterCount={0} rowCount={4} />;
    if (isError) {
        return (
            <EmptyState
                icon={<AlertTriangle />}
                title={`Couldn't load ${what}`}
                description={errorMessage(error, 'The settings service did not answer.')}
                action={<Button size="sm" variant="secondary" onClick={onRetry}>Retry</Button>}
            />
        );
    }
    if (empty) return <EmptyState icon={<Settings2 />} title={`No ${what} returned`} description="The server sent no values for this section." />;
    return <>{children}</>;
}

/* ─── General Tab ───────────────────────────────────── */
// GET /settings. Read-only here: PUT /settings exists, but its fields are not
// specified, so the console shows them rather than guessing a form.
function GeneralTab() {
    const q = useQuery(['settings', 'general'], () => settingsApi.getGeneral(), { retry: 1, refetchOnWindowFocus: false });
    const data = settingsPayload(q.data?.data);
    return (
        <SettingsSection title="Gateway" description={`This ${BRAND.name} instance, as the server reports it. Read-only.`}>
            <QueryStates what="general settings" isLoading={q.isLoading} isError={q.isError} error={q.error} onRetry={() => q.refetch()} empty={!data || Object.keys(data).length === 0}>
                {data && <ConfigFields data={data} />}
            </QueryStates>
        </SettingsSection>
    );
}

/* ─── Auth Tab ──────────────────────────────────────── */
// GET /settings/auth. The provider's health is not reported by any endpoint,
// so nothing here claims the connection is up.
function AuthTab() {
    const q = useQuery(['settings', 'auth'], () => settingsApi.getAuthConfig(), { retry: 1, refetchOnWindowFocus: false });
    const data = settingsPayload(q.data?.data);
    return (
        <div className="space-y-8">
            <SettingsSection title="OIDC / d2auth configuration" description="Identity provider settings for SSO sign-in. Read-only: they are configured on the server.">
                <QueryStates what="authentication settings" isLoading={q.isLoading} isError={q.isError} error={q.error} onRetry={() => q.refetch()} empty={!data || Object.keys(data).length === 0}>
                    {data && <ConfigFields data={data} />}
                </QueryStates>
            </SettingsSection>

            <SettingsSection title="Multi-factor authentication" description="Additional factors beyond SSO.">
                <p className="flex items-center gap-2 text-sm text-[var(--d2d-text-secondary)]">
                    <Lock className="h-4 w-4 flex-none" aria-hidden="true" />
                    MFA is enforced by the identity provider (d2auth), not in this console.
                </p>
            </SettingsSection>
        </div>
    );
}

/* ─── Agents Tab ────────────────────────────────────── */
function AgentsTab() {
    const queryClient = useQueryClient();
    const { confirm } = useDialogs();
    const q = useQuery(['settings', 'agent-tokens'], () => settingsApi.getAgentTokens(), { retry: 1, refetchOnWindowFocus: false });
    const tokens = useMemo(() => normalizeAgentTokens(q.data?.data), [q.data]);

    const rotate = useMutation((t: AgentToken) => settingsApi.rotateAgentToken(t.category), {
        onSuccess: (_res, t) => {
            queryClient.invalidateQueries(['settings', 'agent-tokens']);
            toast.success(`${t.label} token rotated. Give the new token to its agents.`);
        },
        onError: (err, t) => { toast.error(errorMessage(err, `Couldn't rotate the ${t.label} token`)); },
    });

    const handleRotate = async (t: AgentToken) => {
        const ok = await confirm({
            title: `Rotate the ${t.label} token?`,
            message: `The current ${t.label} token stops working at once${t.agents ? `, and the ${t.agents} agent${t.agents === 1 ? '' : 's'} using it disconnect` : ''} until each is given the new token.`,
            confirmLabel: 'Rotate token',
            variant: 'danger',
        });
        if (ok) rotate.mutate(t);
    };

    const origin = window.location.origin;
    const hubUrl = origin.replace(/^http/, 'ws');

    return (
        <div className="space-y-8">
            <SettingsSection title="Agent registration tokens" description="Tokens agents present to register with the hub. Masked; an admin can reveal one to copy it.">
                <QueryStates what="agent tokens" isLoading={q.isLoading} isError={q.isError} error={q.error} onRetry={() => q.refetch()} empty={tokens.length === 0}>
                    <ul className="space-y-3" aria-label="Agent tokens">
                        {tokens.map((t) => (
                            <li key={t.category} className="flex items-center justify-between gap-4 p-4 bg-[var(--d2d-bg-secondary)] rounded-xl">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <p className="text-sm font-medium text-[var(--d2d-text-primary)]">{t.label}</p>
                                        {t.agents !== undefined && (
                                            <span className="px-2 py-0.5 text-micro font-medium bg-[var(--ds-color-bg-subtle)] text-[var(--d2d-text-primary)] rounded-full">
                                                {t.agents} agent{t.agents === 1 ? '' : 's'}
                                            </span>
                                        )}
                                    </div>
                                    <div className="mt-1.5 text-xs text-[var(--d2d-text-muted)]">
                                        <MaskedValue value={t.token} kind="secret" label={`${t.label} token`} empty="No token issued" />
                                    </div>
                                    {t.rotatedAt && (
                                        <p className="mt-1 text-xs text-[var(--d2d-text-muted)]">
                                            Last changed <Timestamp value={t.rotatedAt} fallback={t.rotatedAt} />
                                        </p>
                                    )}
                                </div>
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    leadingIcon={<RefreshCw className="h-3 w-3" />}
                                    onClick={() => handleRotate(t)}
                                    loading={rotate.isLoading && rotate.variables?.category === t.category}
                                    disabled={rotate.isLoading}
                                    aria-label={`Rotate ${t.label} token`}
                                >
                                    Rotate
                                </Button>
                            </li>
                        ))}
                    </ul>
                </QueryStates>
            </SettingsSection>

            <SettingsSection title="Agent install command" description="Run this on a target server to install the SSH agent.">
                <CodeBlock
                    lang="bash"
                    code={`# Install SSH agent on a target server
curl -sSL ${origin}/install-agent.sh | \\
  IAP_HUB_URL=${hubUrl}/agent \\
  IAP_AGENT_TOKEN=<AGENT_TOKEN> \\
  IAP_SERVER_ID=$(hostname) \\
  sudo bash`}
                />
                <p className="mt-2 text-xs text-[var(--d2d-text-muted)]">
                    Replace <code>&lt;AGENT_TOKEN&gt;</code> with the token for the server's category above. Pass it through your secret
                    store rather than your shell history where you can.
                </p>
            </SettingsSection>
        </div>
    );
}

/* ─── Audit Tab ─────────────────────────────────────── */
// GET / PUT /settings/audit. Top-level scalars are editable (numbers, switches,
// text); anything nested is shown read-only. The CSV exports and the
// compliance cards had no endpoint behind them and are gone (F1).
type Scalar = string | number | boolean | null;

function AuditTab() {
    const queryClient = useQueryClient();
    const q = useQuery(['settings', 'audit'], () => settingsApi.getAuditConfig(), { retry: 1, refetchOnWindowFocus: false });
    const data = settingsPayload(q.data?.data);
    const editable = useMemo(
        () => (data ? (Object.entries(data).filter(([, v]) => v === null || ['string', 'number', 'boolean'].includes(typeof v)) as [string, Scalar][]) : []),
        [data],
    );
    // The draft restarts from the server's values whenever they change (load,
    // save, refetch): reset during render, React's "adjust state when a prop
    // changes" pattern.
    const [draft, setDraft] = useState<Record<string, Scalar>>(() => Object.fromEntries(editable));
    const [draftFrom, setDraftFrom] = useState(editable);
    if (draftFrom !== editable) {
        setDraftFrom(editable);
        setDraft(Object.fromEntries(editable));
    }
    const dirty = editable.some(([k, v]) => draft[k] !== v);

    const save = useMutation(() => settingsApi.updateAuditConfig({ ...(data ?? {}), ...draft }), {
        onSuccess: () => {
            queryClient.invalidateQueries(['settings', 'audit']);
            toast.success('Audit settings saved');
        },
        onError: (err) => { toast.error(errorMessage(err, "Couldn't save the audit settings")); },
    });

    return (
        <SettingsSection title="Audit logging" description="Retention and audit event settings.">
            <QueryStates what="audit settings" isLoading={q.isLoading} isError={q.isError} error={q.error} onRetry={() => q.refetch()} empty={!data || Object.keys(data).length === 0}>
                {data && (
                    <form
                        className="space-y-6"
                        onSubmit={(e) => { e.preventDefault(); if (dirty) save.mutate(); }}
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            {editable.map(([key, original]) => {
                                const label = humanize(key);
                                const value = draft[key];
                                if (typeof original === 'boolean') {
                                    return (
                                        <Switch
                                            key={key}
                                            label={label}
                                            checked={value === true}
                                            onChange={(checked) => setDraft((d) => ({ ...d, [key]: checked }))}
                                        />
                                    );
                                }
                                if (typeof original === 'number') {
                                    return (
                                        <Input
                                            key={key}
                                            label={label}
                                            type="number"
                                            value={value === null || value === undefined ? '' : String(value)}
                                            onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value === '' ? null : Number(e.target.value) }))}
                                        />
                                    );
                                }
                                return (
                                    <Input
                                        key={key}
                                        label={label}
                                        value={value === null || value === undefined ? '' : String(value)}
                                        onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
                                    />
                                );
                            })}
                        </div>
                        <ConfigFields data={data} exclude={editable.map(([k]) => k)} />
                        <div className="flex justify-end gap-2">
                            <Button type="button" variant="secondary" disabled={!dirty || save.isLoading} onClick={() => setDraft(Object.fromEntries(editable))}>
                                Discard
                            </Button>
                            <Button type="submit" disabled={!dirty} loading={save.isLoading}>
                                Save
                            </Button>
                        </div>
                    </form>
                )}
            </QueryStates>
        </SettingsSection>
    );
}

/* ─── Integrations Tab ──────────────────────────────── */
function IntegrationsTab() {
    const [expanded, setExpanded] = useState<Record<string, boolean>>({
        k8s: true,
        ssh: false,
        aws: false,
        azure: false,
        gcp: false,
    });

    const toggle = (id: string) =>
        setExpanded((s) => ({ ...s, [id]: !s[id] }));

    const gatewayUrl = window.location.origin;

    return (
        <div className="space-y-6">
            <SettingsSection
                title="Service Integrations"
                description={`Connect your infrastructure to ${BRAND.name}. Click any section for setup instructions.`}
            >
                <div className="space-y-3">
                    {/* ── Kubernetes ── */}
                    <IntegrationCard
                        id="k8s"
                        icon={Cloud}
                        title="Kubernetes — Webhook Token Review"
                        description={`Authenticate kubectl and K8s API requests via ${BRAND.name} tokens`}
                        expanded={expanded.k8s}
                        onToggle={() => toggle('k8s')}
                    >
                        <StepBlock step={1} title="Create webhook config file on the control plane">
                            <CodeBlock
                                lang="yaml"
                                code={`# /etc/kubernetes/iap-webhook-config.yaml
apiVersion: v1
kind: Config
clusters:
  - name: iap-webhook
    cluster:
      server: ${gatewayUrl}/api/v1/k8s/authenticate
      # The CA that signed the gateway's TLS certificate, base64-encoded.
      certificate-authority-data: <GATEWAY_CA_BASE64>
users:
  - name: iap-webhook
current-context: iap-webhook
contexts:
  - context:
      cluster: iap-webhook
      user: iap-webhook
    name: iap-webhook`}
                            />
                        </StepBlock>
                        <StepBlock step={2} title="Add API server flags">
                            <CodeBlock
                                lang="bash"
                                code={`# Add to kube-apiserver manifest or config:
--authentication-token-webhook-config-file=/etc/kubernetes/iap-webhook-config.yaml
--authentication-token-webhook-cache-ttl=2m`}
                            />
                        </StepBlock>
                        <StepBlock step={3} title={`Bind each ${BRAND.name} user to the least access they need`}>
                            <CodeBlock
                                lang="yaml"
                                code={`# rolebinding.yaml: read-only access to one namespace.
# Widen the role or the scope only as far as the user needs.
apiVersion: rbac.authorization.k8s.io/v1
kind: RoleBinding
metadata:
  name: iap-<USER_NAME>-view
  namespace: <NAMESPACE>
subjects:
  - kind: User
    name: <IAP_USER_EMAIL>
    apiGroup: rbac.authorization.k8s.io
roleRef:
  kind: ClusterRole
  name: view
  apiGroup: rbac.authorization.k8s.io`}
                            />
                        </StepBlock>
                        <StepBlock step={4} title="Test with kubectl">
                            <CodeBlock
                                lang="bash"
                                code={`# Configure kubectl to use your ${BRAND.name} token
kubectl config set-credentials iap-user \\
  --token="<your-iap-access-token>"

kubectl config set-context iap-context \\
  --cluster=your-cluster \\
  --user=iap-user

kubectl --context=iap-context get pods`}
                            />
                        </StepBlock>
                        <Banner variant="info" title="Replace the <PLACEHOLDERS> before applying">
                            Keep TLS verification on: give the webhook the gateway's CA rather than{' '}
                            <code>insecure-skip-tls-verify</code>, and grant users a namespaced role, not{' '}
                            <code>cluster-admin</code>.
                        </Banner>
                        <div className="mt-3 p-3 bg-[var(--zta-tint-info)] rounded-lg border border-[var(--zta-tint-info-border)]">
                            <p className="text-xs text-[var(--d2d-info)]">
                                <strong>API Endpoint:</strong>{' '}
                                <code className="bg-[var(--zta-tint-info)] px-1.5 py-0.5 rounded">
                                    POST {gatewayUrl}/api/v1/k8s/authenticate
                                </code>
                            </p>
                        </div>
                    </IntegrationCard>

                    {/* ── SSH Servers ── */}
                    <IntegrationCard
                        id="ssh"
                        icon={Terminal}
                        title="SSH Servers — Certificate Authority Trust"
                        description="Configure servers to accept SSH certificates issued by the gateway"
                        expanded={expanded.ssh}
                        onToggle={() => toggle('ssh')}
                    >
                        <StepBlock step={1} title="One-liner setup (recommended)">
                            <CodeBlock
                                lang="bash"
                                code={`# Run on the target server as root:
sudo bash -c "$(curl -sf ${gatewayUrl}/api/v1/ssh/ca-key-public > /etc/ssh/iap_user_ca.pub && \\
  grep -q TrustedUserCAKeys /etc/ssh/sshd_config || \\
  echo 'TrustedUserCAKeys /etc/ssh/iap_user_ca.pub' >> /etc/ssh/sshd_config && \\
  systemctl restart sshd && echo 'SSH CA trust configured')"`}
                            />
                        </StepBlock>
                        <StepBlock step={2} title="Or use the full setup script">
                            <CodeBlock
                                lang="bash"
                                code={`# Download and run the setup script:
curl -sL ${gatewayUrl}/scripts/setup-ssh-ca-trust.sh | \\
  sudo IAP_URL=${gatewayUrl} bash

# Or if the script is on the server:
sudo bash setup-ssh-ca-trust.sh ${gatewayUrl}`}
                            />
                        </StepBlock>
                        <StepBlock step={3} title="Test certificate-based SSH">
                            <CodeBlock
                                lang="bash"
                                code={`# 1. Issue cert with principal matching the server username
#    (e.g., principal = "azureuser" or "ubuntu")

# 2. Download cert + key from the Certificates page

# 3. Set permissions and SSH:
chmod 600 ssh-*-key
ssh -i ssh-*-key user@your-server-ip`}
                            />
                        </StepBlock>
                    </IntegrationCard>

                    {/* ── AWS RDS ── */}
                    <IntegrationCard
                        id="aws"
                        icon={Database}
                        title="AWS RDS / Aurora — IAM + SSL Authentication"
                        description={`Connect to AWS managed databases using ${BRAND.name} certificates`}
                        expanded={expanded.aws}
                        onToggle={() => toggle('aws')}
                    >
                        <StepBlock step={1} title="Enable IAM authentication on RDS">
                            <CodeBlock
                                lang="bash"
                                code={`# Enable IAM authentication on your RDS instance:
aws rds modify-db-instance \\
  --db-instance-identifier your-instance \\
  --enable-iam-database-authentication \\
  --apply-immediately`}
                            />
                        </StepBlock>
                        <StepBlock step={2} title="Create IAM-authenticated database user">
                            <CodeBlock
                                lang="sql"
                                code={`-- PostgreSQL:
CREATE USER iap_user WITH LOGIN;
GRANT rds_iam TO iap_user;

-- MySQL:
CREATE USER 'iap_user'@'%' IDENTIFIED WITH AWSAuthenticationPlugin AS 'RDS';`}
                            />
                        </StepBlock>
                        <StepBlock step={3} title="Connect with IAM token + SSL">
                            <CodeBlock
                                lang="bash"
                                code={`# Download AWS RDS CA bundle:
curl -o rds-ca.pem https://truststore.pki.rds.amazonaws.com/global/global-bundle.pem

# Generate IAM auth token:
TOKEN=$(aws rds generate-db-auth-token \\
  --hostname your-instance.region.rds.amazonaws.com \\
  --port 5432 --username iap_user)

# Connect with SSL:
PGSSLMODE=verify-full PGSSLROOTCERT=rds-ca.pem \\
  psql "host=your-instance.region.rds.amazonaws.com \\
  port=5432 dbname=mydb user=iap_user password=$TOKEN"`}
                            />
                        </StepBlock>
                    </IntegrationCard>

                    {/* ── Azure SQL ── */}
                    <IntegrationCard
                        id="azure"
                        icon={Database}
                        title="Azure SQL / CosmosDB — AAD + TLS Authentication"
                        description="Connect to Azure managed databases using Azure AD tokens"
                        expanded={expanded.azure}
                        onToggle={() => toggle('azure')}
                    >
                        <StepBlock step={1} title="Enable Azure AD admin on SQL Server">
                            <CodeBlock
                                lang="bash"
                                code={`# Set Azure AD admin:
az sql server ad-admin create \\
  --resource-group your-rg \\
  --server-name your-server \\
  --display-name "${BRAND.name} Admin" \\
  --object-id <aad-user-or-group-object-id>`}
                            />
                        </StepBlock>
                        <StepBlock step={2} title="Create contained database user">
                            <CodeBlock
                                lang="sql"
                                code={`-- Connect as Azure AD admin, then:
CREATE USER [user@yourdomain.com] FROM EXTERNAL PROVIDER;
ALTER ROLE db_datareader ADD MEMBER [user@yourdomain.com];
ALTER ROLE db_datawriter ADD MEMBER [user@yourdomain.com];`}
                            />
                        </StepBlock>
                        <StepBlock step={3} title="Connect with AAD token">
                            <CodeBlock
                                lang="bash"
                                code={`# Get Azure AD token for SQL:
TOKEN=$(az account get-access-token \\
  --resource https://database.windows.net/ \\
  --query accessToken -o tsv)

# Connect with sqlcmd:
sqlcmd -S your-server.database.windows.net \\
  -d your-database \\
  -G --access-token "$TOKEN"

# Or via connection string (TLS enforced by default):
# Server=your-server.database.windows.net;Database=your-db;
# Authentication=Active Directory Access Token;`}
                            />
                        </StepBlock>
                    </IntegrationCard>

                    {/* ── GCP Cloud SQL ── */}
                    <IntegrationCard
                        id="gcp"
                        icon={Database}
                        title="GCP Cloud SQL — IAM + Cloud SQL Proxy"
                        description="Connect to GCP managed databases using IAM authentication"
                        expanded={expanded.gcp}
                        onToggle={() => toggle('gcp')}
                    >
                        <StepBlock step={1} title="Enable IAM authentication">
                            <CodeBlock
                                lang="bash"
                                code={`# Enable IAM authentication on Cloud SQL:
gcloud sql instances patch your-instance \\
  --database-flags cloudsql.iam_authentication=on`}
                            />
                        </StepBlock>
                        <StepBlock step={2} title="Create IAM database user">
                            <CodeBlock
                                lang="bash"
                                code={`# Create IAM user (PostgreSQL):
gcloud sql users create user@your-project.iam \\
  --instance=your-instance \\
  --type=CLOUD_IAM_USER

# Grant permissions in the database:
GRANT ALL PRIVILEGES ON DATABASE mydb TO "user@your-project.iam";`}
                            />
                        </StepBlock>
                        <StepBlock step={3} title="Connect via Cloud SQL Proxy">
                            <CodeBlock
                                lang="bash"
                                code={`# Install Cloud SQL Auth Proxy:
curl -o cloud-sql-proxy \\
  https://storage.googleapis.com/cloud-sql-connectors/cloud-sql-proxy/v2.8.2/cloud-sql-proxy.linux.amd64
chmod +x cloud-sql-proxy

# Start the proxy (handles TLS automatically):
./cloud-sql-proxy \\
  --auto-iam-authn \\
  your-project:your-region:your-instance

# Connect via localhost:
psql "host=127.0.0.1 port=5432 dbname=mydb user=user@your-project.iam"`}
                            />
                        </StepBlock>
                    </IntegrationCard>
                </div>
            </SettingsSection>
        </div>
    );
}

/* ─── Integration Sub-components ────────────────────── */
// A setup guide, not a status: no endpoint reports whether an integration is
// live, so the card claims nothing about it (F1).
function IntegrationCard({
    icon: Icon,
    title,
    description,
    expanded,
    onToggle,
    children,
}: {
    id: string;
    icon: React.ComponentType<{ className?: string }>;
    title: string;
    description: string;
    expanded: boolean;
    onToggle: () => void;
    children: React.ReactNode;
}) {
    return (
        <div className="border border-[var(--d2d-border)] rounded-xl overflow-hidden">
            <CardButton
                onClick={onToggle}
                aria-expanded={expanded}
                className="w-full flex items-center gap-3 p-4 hover:bg-[var(--ds-color-bg-hover)] transition-colors"
            >
                <div className="flex items-center justify-center h-9 w-9 rounded-lg bg-[var(--ds-color-bg-subtle)] text-[var(--d2d-text-primary)]">
                    <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1 text-left">
                    <p className="text-sm font-semibold text-[var(--d2d-text-primary)]">{title}</p>
                    <p className="text-xs text-[var(--d2d-text-muted)]">{description}</p>
                </div>
                {expanded ? <ChevronDown className="h-4 w-4 text-[var(--d2d-text-muted)]" /> : <ChevronRight className="h-4 w-4 text-[var(--d2d-text-muted)]" />}
            </CardButton>
            {expanded && (
                <div className="px-4 pb-4 space-y-4 border-t border-[var(--d2d-border)] pt-4">
                    {children}
                </div>
            )}
        </div>
    );
}

function StepBlock({ step, title, children }: { step: number; title: string; children: React.ReactNode }) {
    return (
        <div>
            <div className="flex items-center gap-2 mb-2">
                <span className="flex items-center justify-center h-5 w-5 rounded-full bg-[var(--ds-color-bg-subtle)] text-[var(--d2d-text-primary)] text-micro font-bold">
                    {step}
                </span>
                <p className="text-xs font-medium text-[var(--d2d-text-secondary)]">{title}</p>
            </div>
            {children}
        </div>
    );
}

/* ─── Shared Components ─────────────────────────────── */
function SettingsSection({
    title,
    description,
    children,
}: {
    title: string;
    description: string;
    children: React.ReactNode;
}) {
    return (
        <div>
            <h3 className="text-base font-medium text-[var(--d2d-text-primary)]">{title}</h3>
            <p className="text-sm text-[var(--d2d-text-muted)] mt-0.5 mb-4">{description}</p>
            {children}
        </div>
    );
}

function ReadOnlyField({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div className="min-w-0">
            <dt className="block text-label font-semibold text-[var(--d2d-text-muted)] mb-1.5 uppercase">{label}</dt>
            <dd className="min-w-0 break-words px-3.5 py-2.5 rounded-xl border text-sm bg-[var(--d2d-bg-secondary)] border-[var(--d2d-border)] text-[var(--d2d-text-secondary)]">
                {children}
            </dd>
        </div>
    );
}
